import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { fetchQuery, querySnapshot, subscribeQuery } from '../services/queryCache';

export default function useQuery(read, params, { keepPreviousData = false } = {}) {
  const query = read.describe(params);
  const current = useRef(query);
  current.current = query;
  const subscribe = useCallback(listener => subscribeQuery(query.key, listener), [query.key]);
  const snapshot = useCallback(() => querySnapshot(query.key), [query.key]);
  const state = useSyncExternalStore(subscribe, snapshot);
  const previous = useRef(null);
  if (state.data !== undefined) previous.current = { data: state.data, key: query.key, params: query.params };
  const data = state.data ?? (keepPreviousData ? previous.current?.data : undefined);
  useEffect(() => {
    const update = () => { if (document.visibilityState !== 'hidden') fetchQuery(current.current).catch(() => {}); };
    fetchQuery(current.current).catch(() => {});
    window.addEventListener('focus', update);
    document.addEventListener('visibilitychange', update);
    return () => { window.removeEventListener('focus', update); document.removeEventListener('visibilitychange', update); };
  }, [query.key]);
  const refresh = useCallback(() => fetchQuery(current.current, { force: true }).catch(() => {}), []);
  return { ...state, data, params: state.data === undefined && keepPreviousData ? previous.current?.params : query.params, loading: data === undefined && !state.error, pending: state.fetching || (state.data === undefined && !state.error), isPreviousData: data !== undefined && state.data === undefined, refresh };
}